import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserData } from '../../firebase/userData';
import { logger } from '../../utils/logger';

export default function PersonalInfoSection({ userProfile, onUpdate }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const personalInfoText = t('account.personalInfo', { returnObjects: true, defaultValue: {} }) || {};

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: userProfile.name || '',
    email: userProfile.email || ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation only (email cannot be changed for security reasons)
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (!user || !user.uid) {
      setErrors({ general: 'User not authenticated. Please sign in again.' });
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setErrors({});

    try {
      // Save to Firestore
      const result = await updateUserData(user.uid, {
        name: formData.name.trim()
      });

      if (result.success) {
        // Update parent component (only name, email cannot be changed)
        onUpdate({
          name: formData.name.trim(),
          email: userProfile.email // Keep original email, don't allow changes
        });
        
        setEditMode(false);
        setSuccessMessage('Profile updated successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(result.error || 'Failed to update profile');
      }
    } catch (error) {
      logger.error('Error updating profile:', error);
      setErrors({ 
        general: error.message || 'Failed to update profile. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: userProfile.name || '',
      email: userProfile.email || ''
    });
    setErrors({});
    setEditMode(false);
    setSuccessMessage('');
  };

  const handleEdit = () => {
    setEditMode(true);
    setSuccessMessage('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-300">
          {personalInfoText.title || 'Personal Information'}
        </h2>
        {!editMode && (
          <button
            onClick={handleEdit}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            {personalInfoText.edit || 'Edit'}
          </button>
        )}
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-900/50 border border-green-500 rounded-lg text-green-300 text-sm">
          {successMessage}
        </div>
      )}

      {errors.general && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-300 text-sm">
          {errors.general}
        </div>
      )}

      <div className="space-y-4">
        {/* Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            {personalInfoText.name || 'Name'}
          </label>
          {editMode ? (
            <>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-4 py-2 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name}</p>
              )}
            </>
          ) : (
            <p className="text-gray-300">{userProfile.name || 'Not set'}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            {personalInfoText.email || 'Email'}
          </label>
          {editMode ? (
            <>
              <input
                type="email"
                value={formData.email}
                readOnly
                disabled
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed opacity-60"
              />
              <p className="mt-2 text-xs text-gray-500">
                {i18n.language === 'es' 
                  ? 'El email no se puede modificar por razones de seguridad. Si ya no tienes acceso a este email, por favor regístrate nuevamente.'
                  : 'Email cannot be modified for security reasons. If you no longer have access to this email, please sign up again.'}
              </p>
            </>
          ) : (
            <p className="text-gray-300">{userProfile.email || 'Not set'}</p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {editMode && (
        <div className="flex items-center gap-4 mt-6">
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : (personalInfoText.save || 'Save Changes')}
          </button>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {personalInfoText.cancel || 'Cancel'}
          </button>
        </div>
      )}
    </div>
  );
}









